import {
    Integrations as BaseIntegrations,
    init,
    reactRouterV5Instrumentation,
    setTag as setMonitorTag,
} from '@sentry/react';
import { Integrations as TracingIntegrations } from '@sentry/tracing';
import { withoutGraphQLFetch } from 'apollo-link-sentry';
import { getApp } from '@lingoda/env';
import { beforeBreadcrumb } from './breadcrumbs';
import { setUserAgentClientHintsTags } from './setUserAgentClientHintsTags';

const GLOBAL_ERROR_NAMES_TO_IGNORE = [
    'ChunkLoadError',
    'React ErrorBoundary Error',
    'top.GLOBALS',
    'Non-Error exception captured',
    `TypeError: null is not an object (evaluating 'n.title')`,
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications.',
    'This class is no longer available', // LW-24978 known issue when 2 teacher trying to book the same class
    `Can't find variable: disableWebkitEnterFullscreen`, // LW-29773 third party issue
];

const escapeRegExp = (text: string | undefined = '') =>
    text.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&');

type TrackerConfig = {
    readonly deployEnvironment: string;
    readonly releaseId: string;
    readonly kinesisEndpoint: string;
    readonly sentryDsn: string;
    readonly sentryTunnelEndpoint?: string;
    readonly errorNamesToIgnore?: readonly string[];
};

export const initErrorTracker = (config: TrackerConfig, history: RouterHistory) => {
    const { errorNamesToIgnore = [] } = config;
    init({
        dsn: config.sentryDsn,
        tunnel: config.sentryTunnelEndpoint,
        environment: config.deployEnvironment,
        release: config.releaseId,
        ignoreErrors: GLOBAL_ERROR_NAMES_TO_IGNORE.concat(errorNamesToIgnore),
        allowUrls: [/lingoda\.(com|xyz|org|wip)/i],
        integrations: [
            new BaseIntegrations.GlobalHandlers({
                onerror: true,
                onunhandledrejection: false,
            }),
            new TracingIntegrations.BrowserTracing({
                // Exclude Kinesis endpoint and exp-lingoda.com (Kameleoon SDK) to avoid CORS issues with sentry-trace headers
                tracePropagationTargets: [
                    /^\//,
                    new RegExp(
                        `^(?!${escapeRegExp(
                            config.kinesisEndpoint,
                        )})(?!.*exp-lingoda\\.com).*lingoda.*`,
                    ),
                ],
                // TODO: We should also pass static routes and matchPath, but couldn't get past type errors…
                routingInstrumentation: reactRouterV5Instrumentation(history),
            }),
        ],
        attachStacktrace: true,
        tracesSampler: (context) => {
            if (context.transactionContext.metadata?.sampleRate) {
                return context.transactionContext.metadata.sampleRate;
            }

            return 0;
        },
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,
        beforeBreadcrumb: withoutGraphQLFetch(beforeBreadcrumb),
    });

    setMonitorTag('lingoda.app', getApp());
    setMonitorTag('deviceMemory', navigator.deviceMemory ?? 'unsupported');
    setMonitorTag('cpuLogicalCores', navigator.hardwareConcurrency);

    setUserAgentClientHintsTags().catch(console.error);
};

type RouterHistory = Parameters<typeof reactRouterV5Instrumentation>[0];
